import { supabase } from '../lib/supabase';
import {
  PlayerStatistics,
  GameStatistics,
  BreakStatistics,
  ScoringStatistics,
  BallPottingStatistics,
  BreakDistribution,
  RecentForm,
} from '../types';

/**
 * Get complete player statistics
 */
export async function getPlayerStatistics(playerId: number): Promise<PlayerStatistics | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_statistics', {
      p_player_id: playerId,
    });

    if (error) {
      console.error('Error fetching player statistics:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Transform the JSONB response to typed structure
    return {
      playerId: data.playerId || playerId,
      games: data.games || {
        totalGames: 0,
        gamesWon: 0,
        gamesLost: 0,
        winRate: 0,
        totalFrames: 0,
        framesWon: 0,
        framesLost: 0,
        frameWinRate: 0,
      },
      breaks: data.breaks || {
        highestBreak: 0,
        totalBreaks: 0,
        centuryBreaks: 0,
        fiftyPlusBreaks: 0,
        averageBreak: 0,
      },
      scoring: data.scoring || {
        totalPoints: 0,
        averagePointsPerFrame: 0,
        averagePointsPerGame: 0,
      },
      calculatedAt: data.calculatedAt || new Date().toISOString(),
    };
  } catch (err) {
    console.error('Failed to fetch player statistics:', err);
    return null;
  }
}

/**
 * Get game statistics only
 */
export async function getPlayerGameStats(playerId: number): Promise<GameStatistics | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_game_stats', {
      p_player_id: playerId,
    });

    if (error) {
      console.error('Error fetching game statistics:', error);
      throw error;
    }

    return data || null;
  } catch (err) {
    console.error('Failed to fetch game statistics:', err);
    return null;
  }
}

/**
 * Get break statistics only
 */
export async function getPlayerBreakStats(playerId: number): Promise<BreakStatistics | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_break_stats', {
      p_player_id: playerId,
    });

    if (error) {
      console.error('Error fetching break statistics:', error);
      throw error;
    }

    return data || null;
  } catch (err) {
    console.error('Failed to fetch break statistics:', err);
    return null;
  }
}

/**
 * Get scoring statistics only
 */
export async function getPlayerScoringStats(playerId: number): Promise<ScoringStatistics | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_scoring_stats', {
      p_player_id: playerId,
    });

    if (error) {
      console.error('Error fetching scoring statistics:', error);
      throw error;
    }

    return data || null;
  } catch (err) {
    console.error('Failed to fetch scoring statistics:', err);
    return null;
  }
}

/**
 * Get ball potting statistics
 */
export async function getPlayerBallPottingStats(playerId: number): Promise<BallPottingStatistics | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_ball_potting_stats', {
      p_player_id: playerId,
    });

    if (error) {
      console.error('Error fetching ball potting statistics:', error);
      throw error;
    }

    return data || null;
  } catch (err) {
    console.error('Failed to fetch ball potting statistics:', err);
    return null;
  }
}

/**
 * Get break distribution
 */
export async function getPlayerBreakDistribution(playerId: number): Promise<BreakDistribution | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_break_distribution', {
      p_player_id: playerId,
    });

    if (error) {
      console.error('Error fetching break distribution:', error);
      throw error;
    }

    return data || null;
  } catch (err) {
    console.error('Failed to fetch break distribution:', err);
    return null;
  }
}

/**
 * Get recent form (last N games)
 */
export async function getPlayerRecentForm(playerId: number, limit: number = 5): Promise<RecentForm | null> {
  try {
    const { data, error } = await supabase.rpc('get_player_recent_form', {
      p_player_id: playerId,
      p_limit: limit,
    });

    if (error) {
      console.error('Error fetching recent form:', error);
      throw error;
    }

    return data || null;
  } catch (err) {
    console.error('Failed to fetch recent form:', err);
    return null;
  }
}

/**
 * Get complete player statistics including extended data (ball potting, distribution, form)
 */
export async function getCompletePlayerStatistics(playerId: number): Promise<PlayerStatistics | null> {
  try {
    // Get base statistics
    const baseStats = await getPlayerStatistics(playerId);
    
    if (!baseStats) {
      return null;
    }

    // Get extended statistics in parallel
    const [ballPotting, breakDistribution, recentForm] = await Promise.all([
      getPlayerBallPottingStats(playerId),
      getPlayerBreakDistribution(playerId),
      getPlayerRecentForm(playerId, 10),
    ]);

    return {
      ...baseStats,
      ballPotting: ballPotting || undefined,
      breakDistribution: breakDistribution || undefined,
      recentForm: recentForm || undefined,
    };
  } catch (err) {
    console.error('Failed to fetch complete player statistics:', err);
    return null;
  }
}

